import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const CartDrawer = ({ onClose }) => {
  const { cart, updateQuantity, removeFromCart, cartSubtotal, shippingFee, cartTotal, cartCount } = useCart();
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    if (onClose) onClose();
    navigate(path);
  };

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 px-4">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <ShoppingBag className="w-10 h-10 text-slate-400" />
        </div>
        <p className="text-slate-600 font-medium mb-2">Your cart is empty</p>
        <p className="text-slate-400 text-sm text-center mb-6">Add items to get started</p>
        <Button 
          onClick={() => handleNavigate('/products')} 
          className="bg-[#0047AB] hover:bg-[#003380]"
          data-testid="browse-products-btn"
        >
          Browse Products
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Sticky Checkout Button at Top - Always Visible */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-600">{cartCount} item{cartCount > 1 ? 's' : ''}</span>
          <span className="font-outfit font-bold text-lg text-[#0047AB]">
            ₹{cartTotal.toLocaleString()}
          </span>
        </div>
        <Button 
          className="w-full bg-[#0047AB] hover:bg-[#003380] text-white h-12 text-base font-semibold shadow-lg shadow-blue-900/20"
          onClick={() => handleNavigate('/cart')}
          data-testid="proceed-checkout-btn"
        >
          Proceed to Checkout
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>

      {/* Cart Items - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <AnimatePresence>
          {cart.map((item, index) => (
            <motion.div 
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.05 }}
              className="flex gap-3 bg-slate-50 rounded-xl p-3 mb-3"
              data-testid={`cart-item-${item.id}`}
            >
              <img 
                src={item.image_url} 
                alt={item.name}
                className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-outfit font-semibold text-slate-900 text-sm line-clamp-2 leading-tight">
                  {item.name}
                </h4>
                <p className="text-[#0047AB] font-bold mt-1">
                  ₹{item.price.toLocaleString()}
                </p>
                
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7 rounded-full"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      data-testid={`decrease-qty-${item.id}`}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="w-8 text-center font-semibold text-sm">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7 rounded-full"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      data-testid={`increase-qty-${item.id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeFromCart(item.id)}
                    data-testid={`remove-item-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Bottom Summary */}
      <div className="border-t border-slate-200 px-4 py-4 bg-white">
        <div className="flex justify-between items-center text-sm text-slate-600 mb-2">
          <span>Subtotal</span>
          <span>₹{cartSubtotal.toLocaleString()}</span>
        </div>
        <div className="flex justify-between items-center text-sm mb-2">
          <span className="flex items-center gap-1 text-slate-600">
            <Truck className="w-3.5 h-3.5" />
            Shipping
          </span>
          <span className={shippingFee === 0 ? 'text-green-600 font-medium' : 'text-slate-600'}>
            {shippingFee === 0 ? 'Free' : `₹${shippingFee.toLocaleString()}`}
          </span>
        </div>
        <div className="flex justify-between items-center mb-3 pt-2 border-t border-slate-100">
          <span className="text-slate-900 font-medium">Total</span>
          <span className="font-outfit font-bold text-xl text-[#0047AB]">
            ₹{cartTotal.toLocaleString()}
          </span>
        </div>
        <Button 
          variant="outline"
          className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 h-11"
          onClick={() => handleNavigate('/cart')}
          data-testid="view-cart-btn"
        >
          View Full Cart
        </Button>
      </div>
    </div>
  );
};

export default CartDrawer;
